import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService } from '../services/api.service';
import { TranslateService } from '../services/translate.service';

@Component({
  selector: 'app-payroll-profiles',
  templateUrl: './payroll-profiles.component.html',
  styleUrls: ['./payroll-profiles.component.scss']
})
export class PayrollProfilesComponent implements OnInit {

  employees: any[] = [];
  selectedEmpId: number | null = null;
  profiles: any[] = [];
  isLoading = false;
  employeeSearch: string = '';

  // form model for add/edit
  editingProfile: any = null;
  editMode: 'add' | 'edit' = 'add';

  // loan/installment plan model
  loanPlans: any[] = [];
  newLoan: any = {
    description: '',
    totalAmount: null,
    totalMonths: null,
    startMonthYear: ''
  };

  constructor(
    private apiService: ApiService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.loadEmployees();
  }

  loadEmployees(): void {
    this.apiService.getEmplyees().subscribe(res => {
      const data: any = res || {};
      this.employees = data.employees || [];
    });
  }

  get filteredEmployees(): any[] {
    const term = (this.employeeSearch || '').toLowerCase().trim();
    if (!term) {
      return this.employees;
    }
    return this.employees.filter(e => {
      const id = e.employeeId != null ? String(e.employeeId) : '';
      const name = (e.employeeName || e.name || '').toLowerCase();
      return id.includes(term) || name.includes(term);
    });
  }

  onEmployeeChange(): void {
    if (this.selectedEmpId) {
      this.editingProfile = null;
      this.loadProfiles();
      this.loadLoanPlan();
    } else {
      this.profiles = [];
      this.loanPlans = [];
    }
  }

  loadProfiles(): void {
    if (!this.selectedEmpId) {
      return;
    }
    this.isLoading = true;
    this.apiService.getPayrollProfilesForEmployee(this.selectedEmpId).subscribe(
      data => {
        this.profiles = data || [];
        this.isLoading = false;
      },
      _ => {
        this.isLoading = false;
        this.snackBar.open(this.translate.t('FAILED_LOAD_PROFILES'), 'Close', { duration: 3000 });
      }
    );
  }

  loadLoanPlan(): void {
    if (!this.selectedEmpId) {
      return;
    }
    this.apiService.getPayrollLoanPlan(this.selectedEmpId).subscribe(
      data => {
        this.loanPlans = data || [];
      },
      _ => {
        this.snackBar.open(this.translate.t('FAILED_LOAD_PROFILES'), 'Close', { duration: 3000 });
      }
    );
  }

  startAdd(): void {
    if (!this.selectedEmpId) {
      this.snackBar.open(this.translate.t('SELECT_EMPLOYEE_FIRST'), 'Close', { duration: 3000 });
      return;
    }
    this.editMode = 'add';
    this.editingProfile = {
      name: '',
      type: 'ALLOWANCE',
      amount: 0,
      isPercentage: false,
      active: true
    };
  }

  startEdit(p: any): void {
    this.editMode = 'edit';
    this.editingProfile = { ...p };
  }

  cancelEdit(): void {
    this.editingProfile = null;
  }

  saveProfile(): void {
    if (!this.selectedEmpId || !this.editingProfile) {
      return;
    }

    if (!this.editingProfile.name || !this.editingProfile.type) {
      this.snackBar.open(this.translate.t('ENTER_NAME_AND_TYPE'), 'Close', { duration: 3000 });
      return;
    }

    const action$ = this.editMode === 'add'
      ? this.apiService.addPayrollProfileForEmployee(this.selectedEmpId, this.editingProfile)
      : this.apiService.updatePayrollProfile(this.editingProfile);

    action$.subscribe(
      _ => {
        this.snackBar.open(this.translate.t('PROFILE_SAVED'), 'Close', { duration: 3000 });
        this.editingProfile = null;
        this.loadProfiles();
      },
      _ => {
        this.snackBar.open(this.translate.t('FAILED_SAVE_PROFILE'), 'Close', { duration: 3000 });
      }
    );
  }

  deactivate(p: any): void {
    if (!p || !p.id) {
      return;
    }
    if (!confirm(this.translate.t('CONFIRM_DEACTIVATE_PROFILE'))) {
      return;
    }
    this.apiService.deactivatePayrollProfile(p.id).subscribe(
      _ => {
        this.snackBar.open(this.translate.t('PROFILE_DEACTIVATED'), 'Close', { duration: 3000 });
        this.loadProfiles();
      },
      _ => {
        this.snackBar.open(this.translate.t('FAILED_DEACTIVATE_PROFILE'), 'Close', { duration: 3000 });
      }
    );
  }

  // Loan/installment plan helpers

  createLoanPlan(): void {
    if (!this.selectedEmpId) {
      this.snackBar.open(this.translate.t('SELECT_EMPLOYEE_FIRST'), 'Close', { duration: 3000 });
      return;
    }

    if (!this.newLoan.description || !this.newLoan.totalAmount || !this.newLoan.totalMonths || !this.newLoan.startMonthYear) {
      this.snackBar.open(this.translate.t('LOAN_ENTER_ALL_FIELDS'), 'Close', { duration: 3000 });
      return;
    }

    const body = {
      description: this.newLoan.description,
      totalAmount: this.newLoan.totalAmount,
      totalMonths: this.newLoan.totalMonths,
      startMonthYear: this.newLoan.startMonthYear
    };

    this.apiService.createPayrollLoanPlan(this.selectedEmpId, body).subscribe(
      _ => {
        this.snackBar.open(this.translate.t('PROFILE_SAVED'), 'Close', { duration: 3000 });
        this.newLoan = { description: '', totalAmount: null, totalMonths: null, startMonthYear: '' };
        this.loadLoanPlan();
      },
      err => {
        const msg = err?.error || this.translate.t('FAILED_SAVE_PROFILE');
        this.snackBar.open(msg, 'Close', { duration: 3000 });
      }
    );
  }

  cancelLoan(plan: any): void {
    if (!plan || !plan.planId) {
      return;
    }
    this.apiService.cancelPayrollLoanPlan(plan.planId).subscribe(
      _ => {
        this.snackBar.open(this.translate.t('PROFILE_SAVED'), 'Close', { duration: 3000 });
        this.loadLoanPlan();
      },
      _ => {
        this.snackBar.open(this.translate.t('FAILED_SAVE_PROFILE'), 'Close', { duration: 3000 });
      }
    );
  }

  payoffLoan(plan: any): void {
    if (!plan || !plan.planId) {
      return;
    }
    this.apiService.payoffPayrollLoanPlan(plan.planId).subscribe(
      _ => {
        this.snackBar.open(this.translate.t('PROFILE_SAVED'), 'Close', { duration: 3000 });
        this.loadLoanPlan();
      },
      _ => {
        this.snackBar.open(this.translate.t('FAILED_SAVE_PROFILE'), 'Close', { duration: 3000 });
      }
    );
  }
}
